/**
 * The desktop app bar: the brand, the main pages as tabs, and Log time and
 * your account (with sync and Settings) at the far end.
 *
 * The pages used to be the top of the sidebar, with the account row at its
 * foot, and the project tree had to fit between them. Up here they take one
 * line of height and give the sidebar wholly to projects — the part that keeps
 * growing.
 *
 * The pages are folder tabs along the bar's bottom edge. The selected one is a
 * coloured folder: solid accent, standing on an accent band that runs across
 * the top of the page, so tab and page read as one piece. Pages with
 * tabs of their own (Reports, Expenses, Settings) keep them small and
 * underlined under the title — a different shape for "which view of this
 * page" than for "where am I in the app".
 */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { formatElapsed, useTimer } from "@/context/TimerContext";
import { useAllItems, useProjects } from "@/hooks/useData";
import { sinceLabel, useAutoSync, type AutoSyncState } from "@/hooks/useAutoSync";
import { useWorkBlockAutoRun } from "@/hooks/useWorkBlockAutoRun";
import { useCalendarSync } from "@/lib/calendarDay";
import { isOverdue, isToday } from "@/lib/format";
import { appMode } from "@/lib/pocketbase";
import { inNavOrder, useNavOrder } from "@/lib/theme";

import { ExpensesIcon } from "./ExpenseIcons";
import { FolderIcon, Logo } from "./icons";
import { CalendarIcon, ClientsIcon, PlannerIcon, ReportsIcon, SettingsIcon, StopwatchIcon, SyncIcon, TimeIcon, TodayIcon } from "./NavIcons";
import { QuickLogBar } from "./QuickLogBar";

/**
 * The main pages. Projects sits with them: the sidebar's tree is for jumping
 * to one project, this tab is the whole list with its grouping and filters.
 * `also` lists other paths the tab stays lit for — a single project page
 * (/project/:id) belongs under Projects.
 *
 * This is the built-in order; Settings › General can rearrange it (lib/theme,
 * `useNavOrder`).
 */
export const NAV: { to: string; label: string; Icon: (p: { size?: number }) => JSX.Element; end: boolean; also?: string[] }[] = [
  { to: "/", label: "Today", Icon: CalendarIcon, end: true },
  { to: "/time", label: "Time", Icon: TimeIcon, end: false },
  // The list's scopes all live under Tasks; /today is its Today scope.
  { to: "/today", label: "Tasks", Icon: TodayIcon, end: false, also: ["/week", "/next", "/overdue", "/all", "/upcoming", "/calendar"] },
  { to: "/clients", label: "Clients", Icon: ClientsIcon, end: false },
  { to: "/projects", label: "Projects", Icon: FolderIcon, end: false, also: ["/project/"] },
  { to: "/expenses", label: "Expenses", Icon: ExpensesIcon, end: false },
  { to: "/planner", label: "Planner", Icon: PlannerIcon, end: false },
  { to: "/reports", label: "Reports", Icon: ReportsIcon, end: false },
];

export function TopNav() {
  // Mounted once for the desktop shell, so this is where the background
  // Todoist sync lives.
  const sync = useAutoSync();
  // And the coming week's work blocks, once your planning time has passed.
  useWorkBlockAutoRun();
  const { data: items = [] } = useAllItems();
  const todayCount = items.filter((i) => i.status !== "done" && (isOverdue(i) || isToday(i.due))).length;
  const { pathname } = useLocation();
  const [navOrder] = useNavOrder();
  const tabsInOrder = inNavOrder(NAV, navOrder);
  const isCurrent = (n: (typeof NAV)[number], active: boolean) => active || !!n.also?.some((p) => pathname.startsWith(p));

  /*
   * When the tabs and the tools at the end won't both fit, the tabs give way:
   * tabs drop their labels to an icon, from the right end inward, only as many
   * as it takes (the current tab always keeps its label; a collapsed one keeps
   * its name as the tooltip and the accessible name). The tools never move —
   * Log time is the thing you reach for most, so it stays where it always is.
   *
   * Measured, not set at a breakpoint: what fits depends on the labels, the
   * badge, the window and whether a timer pill is showing. A collapsed label
   * is clipped to zero width rather than removed, so its scrollWidth still
   * gives the width it needs, and every tab's width both ways is known whatever
   * state it's in — the same sum decides closing and reopening, so nothing
   * flickers at the edge.
   */
  const bar = useRef<HTMLDivElement>(null);
  const brand = useRef<HTMLAnchorElement>(null);
  const nav = useRef<HTMLElement>(null);
  const tools = useRef<HTMLDivElement>(null);
  const [iconOnly, setIconOnly] = useState<string>(""); // tab paths, comma-joined
  useLayoutEffect(() => {
    const el = bar.current, tabsEl = nav.current;
    if (!el || !tabsEl) return;
    const measure = () => {
      const tabs = Array.from(tabsEl.children) as HTMLElement[];
      const current = tabs.find((t) => t.classList.contains("is-current"));
      const gap = parseFloat(getComputedStyle(current ?? tabs[0]).columnGap) || 0;
      const widths = tabs.map((t) => {
        const label = t.querySelector<HTMLElement>(".topnav-label")!;
        const badge = !!t.querySelector(".topnav-badge");
        const extra = label.scrollWidth + gap * (badge ? 2 : 1) - (badge ? BADGE_NUDGE : 0);
        const collapsed = t.classList.contains("is-iconic");
        return collapsed
          ? { full: t.offsetWidth + extra, icon: t.offsetWidth }
          : { full: t.offsetWidth, icon: t.offsetWidth - label.offsetWidth - gap * (badge ? 2 : 1) + (badge ? BADGE_NUDGE : 0) };
      });
      const cs = getComputedStyle(el);
      const room = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)
        - (brand.current?.offsetWidth ?? 0) - BAR_GAP * 2 - (tools.current?.offsetWidth ?? 0) - 8
        - TAB_GAP * Math.max(0, tabs.length - 1);
      let total = widths.reduce((n, w) => n + w.full, 0);
      const collapse: string[] = [];
      for (let i = tabs.length - 1; i >= 0 && total > room; i--) {
        if (tabs[i] === current) continue;
        total -= widths[i].full - widths[i].icon;
        collapse.push(tabsInOrder[i].to);
      }
      setIconOnly(collapse.join(","));
    };
    measure();
    // The observer catches the tools changing size (a timer starting); the
    // window event is the plain resize, and still arrives when the page isn't
    // painting, which observer callbacks don't.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (tools.current) ro.observe(tools.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [todayCount, pathname, navOrder]);
  const iconic = new Set(iconOnly ? iconOnly.split(",") : []);

  return (
    // Positioned above the page so the selected tab can overlap the page's
    // top border (the page header is sticky at z-index 10).
    <div ref={bar} className="no-print" style={{
      display: "flex", alignItems: "center", gap: BAR_GAP, flex: "none",
      height: 56, padding: "0 16px 0 18px", background: "var(--bg2)",
      position: "relative", zIndex: 20,
    }}>
      {/* The brand is the way home: Today, the day view. */}
      <Link ref={brand} to="/" title="Today" className="topnav-brand" style={{
        display: "flex", alignItems: "center", gap: 10, flex: "none",
        textDecoration: "none", color: "inherit", borderRadius: 10,
      }}>
        <Logo size={30} radius={9} />
        <div className="topnav-name" style={{ minWidth: 0, lineHeight: 1.2 }}>
          <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", letterSpacing: "-0.01em" }}>Project Hub</div>
          <div className="topnav-sub" style={{ fontSize: 11, color: "var(--muted)" }}>Vandehey Strategies</div>
        </div>
      </Link>

      {/* Tabs stand on the bar's bottom edge. No overflow clipping: the selected
          tab reaches 1px below the bar, onto the page's border line. The first
          tab sits flush with the page's left edge; the page's top-left corner
          is square so nothing clips it. */}
      <nav ref={nav} aria-label="Main"
        style={{ display: "flex", alignItems: "flex-end", alignSelf: "stretch", gap: TAB_GAP, flex: 1, minWidth: 0 }}>
        {tabsInOrder.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} title={iconic.has(n.to) ? n.label : undefined}
            className={({ isActive }) => `topnav-tab${isCurrent(n, isActive) ? " is-current" : iconic.has(n.to) ? " is-iconic" : ""}`}
            style={({ isActive }) => tab(isCurrent(n, isActive))}>
            <span className="topnav-icon" style={{ display: "inline-flex" }}><n.Icon size={17} /></span>
            <span className="topnav-label">{n.label}</span>
            {n.to === "/today" && todayCount > 0 && (
              <span className="mono topnav-badge" style={{
                minWidth: 18, textAlign: "center", padding: "1px 6px", borderRadius: 20, fontSize: 10.5,
                background: "var(--accentTint)", color: "var(--accent)",
              }}>{todayCount}</span>
            )}
          </NavLink>
        ))}
      </nav>

      <div ref={tools} style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
        <QuickAddButton />
        <LogTime />
        <AccountMenu sync={sync} />
      </div>
    </div>
  );
}

const BAR_GAP = 18;
const PANEL_W = 560;
const TAB_GAP = 3;
/** The badge's left margin on a collapsed tab (index.css, .is-iconic). */
const BADGE_NUDGE = 4;

/**
 * You, sync and Settings, behind your initial.
 *
 * Sync used to be its own button beside it. Todoist and your calendars both
 * refresh by themselves (on load, on returning to the tab, and the calendars
 * every five minutes), so "Sync now" is rarely needed and doesn't earn a
 * permanent place — a dot on the avatar says when either is syncing or last
 * failed, and the menu says when each last ran.
 */
function AccountMenu({ sync }: { sync: AutoSyncState }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const live = appMode !== "demo";
  const cal = useCalendarSync();

  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const down = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("keydown", key);
    document.addEventListener("mousedown", down);
    return () => { document.removeEventListener("keydown", key); document.removeEventListener("mousedown", down); };
  }, [open]);
  useEffect(() => setOpen(false), [pathname]);

  const name = (user as { name?: string } | null)?.name || "";
  const email = (user as { email?: string } | null)?.email || "";
  const displayName = name || email || (live ? "Account" : "Demo user");
  const initial = String(displayName).trim().charAt(0).toUpperCase() || "U";
  const onSettings = pathname.startsWith("/settings");
  const failed = (live && !!sync.error) || (cal.connected && !!cal.error);
  const busy = (live && sync.syncing) || cal.syncing;

  return (
    <div ref={wrap} style={{ position: "relative" }}>
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="menu"
        title={failed ? `${displayName} · last sync failed` : displayName}
        style={{
          display: "flex", alignItems: "center", gap: 5, padding: "4px 8px 4px 4px", borderRadius: 999, border: "none",
          cursor: "pointer", fontFamily: "inherit",
          color: onSettings || open ? "var(--accent)" : "var(--ink2)",
          background: onSettings || open ? "var(--accentTint)" : "transparent",
        }}>
        <span style={{
          position: "relative", width: 28, height: 28, borderRadius: "50%", background: "var(--accentTint)", color: "var(--accent)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 500,
        }}>
          {initial}
          {(failed || busy) && (
            <span aria-hidden style={{
              position: "absolute", right: -1, bottom: -1, width: 9, height: 9, borderRadius: "50%",
              background: failed ? "var(--warn)" : "var(--accent)", border: "2px solid var(--bg2)",
            }} />
          )}
        </span>
        <span aria-hidden style={{ fontSize: 10, lineHeight: 1 }}>▾</span>
      </button>

      {open && (
        <div role="menu" style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 45, width: 260,
          background: "var(--raised)", border: "1px solid var(--overlayLine)", borderRadius: 12,
          boxShadow: "var(--shadowOverlay)", padding: 6,
        }}>
          <div style={{ padding: "8px 10px 10px", borderBottom: "1px solid var(--line)" }}>
            <div style={{ fontSize: 13.5, fontWeight: "var(--fw-title)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</div>
            {name && email && <div style={{ fontSize: 12, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>}
          </div>

          {live && (
            <SyncRow label="Todoist" syncing={sync.syncing} error={sync.error}
              status={`Pulled ${sinceLabel(sync.lastSyncedAt)}`} onSync={() => void sync.syncNow()} />
          )}
          {cal.connected && (
            <SyncRow label="Calendars" syncing={cal.syncing} error={cal.error}
              status={cal.lastSyncedAt ? `Read ${sinceLabel(cal.lastSyncedAt)}` : "Not read yet"} onSync={() => void cal.syncNow()} />
          )}

          <NavLink role="menuitem" to="/settings" style={{
            display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", marginTop: 2, borderRadius: 8,
            textDecoration: "none", color: "var(--ink2)", fontSize: 13,
          }} className="menu-item">
            <SettingsIcon size={16} /> Settings
          </NavLink>
        </div>
      )}
    </div>
  );
}

/** One source in the account menu: what it is, when it last ran, Sync now. */
function SyncRow({ label, status, syncing, error, onSync }: {
  label: string; status: string; syncing: boolean; error: string | null; onSync: () => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 10px", borderBottom: "1px solid var(--line)" }}>
      <span style={{ display: "flex", color: error ? "var(--warn)" : "var(--ink3)", animation: syncing ? "ph-spin 1s linear infinite" : undefined }}>
        <SyncIcon size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.35 }}>
        <div style={{ color: "var(--ink2)" }}>{label}</div>
        <div style={{ fontSize: 11.5, color: error ? "var(--warn)" : "var(--muted)", overflowWrap: "anywhere" }}>
          {syncing ? "Syncing…" : error ? `Last sync failed — ${error}` : status}
        </div>
      </div>
      <button role="menuitem" onClick={onSync} disabled={syncing} style={{
        flex: "none", padding: "5px 11px", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)",
        color: "var(--ink2)", fontSize: 12, cursor: syncing ? "default" : "pointer", opacity: syncing ? 0.6 : 1,
      }}>Sync now</button>
    </div>
  );
}

function tab(active: boolean): CSSProperties {
  return {
    // Padding lives in index.css (.topnav-tab), where it tightens on a narrower
    // window so seven tabs still fit without the bar scrolling; the hover
    // shade on the tabs behind is there too.
    display: "inline-flex", alignItems: "center", gap: 7, flex: "none",
    borderRadius: "10px 10px 0 0", textDecoration: "none", whiteSpace: "nowrap",
    border: `1px solid ${active ? "var(--accent)" : "var(--line)"}`, borderBottom: "none",
    fontSize: 13.5, fontWeight: active ? "var(--fw-title)" : "var(--fw-medium)",
    // A coloured folder: the front tab is solid accent and stands on the
    // accent band across the top of the page (App.tsx), so tab and band read
    // as one piece — the page's coloured edge. The tabs behind sit in the tint.
    background: active ? "var(--accent)" : "var(--tint)",
    color: active ? "var(--onAccent)" : "var(--ink3)",
    position: "relative", zIndex: active ? 1 : 0,
  };
}

/**
 * Logging time, from anywhere, without its controls sitting on every page.
 *
 * The quick-log bar used to be a band at the top of each page, its dropdowns
 * styled like the page's own and easily mistaken for them. Now it opens from
 * here, on a click or the T key, and closes once something is logged. A
 * running timer stays in view as a pill — the part worth always seeing —
 * with Stop right on it.
 */
function LogTime() {
  const timer = useTimer();
  const { data: projects = [] } = useProjects();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  // T opens it — unless you are typing somewhere, or holding a modifier.
  // Esc and a click elsewhere close it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key !== "t" && e.key !== "T") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName))) return;
      e.preventDefault();
      setOpen(true);
    };
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, []);

  // Put the cursor in the first field, so T then typing just works.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => wrap.current?.querySelector<HTMLElement>(".logtime-panel .ql-note, .logtime-panel button")?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  // Where the panel goes: under the button, its right edge on the button's,
  // pulled left as far as it needs to stay on screen. Read when it opens and
  // on a resize while open.
  const [panelAt, setPanelAt] = useState({ top: 66, right: 16 });
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = wrap.current?.getBoundingClientRect();
      if (!r) return;
      const w = Math.min(PANEL_W, window.innerWidth - 24);
      const right = Math.min(Math.max(12, window.innerWidth - r.right), window.innerWidth - w - 12);
      setPanelAt({ top: r.bottom + 10, right: Math.max(12, right) });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);

  const running = timer.running;
  const what = projects.find((p) => p.id === timer.meta.projectId)?.name ?? timer.meta.category ?? "Ad-hoc";

  return (
    <div ref={wrap} style={{ position: "relative" }}>
      {running ? (
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 2, borderRadius: 999,
          background: "var(--accentTint)", border: "1px solid var(--accentLine)",
        }}>
          <button onClick={() => setOpen((v) => !v)} title="Timer running — details" style={{
            ...pillBtn, color: "var(--accent)", maxWidth: 240,
          }}>
            <StopwatchIcon size={16} />
            <span className="mono" style={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>{formatElapsed(timer.seconds)}</span>
            <span className="logtime-what" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--ink2)" }}>· {what}</span>
          </button>
          <button onClick={() => void timer.stop()} title="Stop and log" style={{
            ...pillBtn, padding: "5px 11px", marginRight: 3, background: "var(--accent)", color: "var(--onAccent)", fontWeight: 500,
          }}>Stop</button>
        </span>
      ) : (
        <button onClick={() => setOpen((v) => !v)} aria-expanded={open} title="Log time (T)" style={{
          ...pillBtn, border: "1px solid var(--line2)", background: open ? "var(--accentTint)" : "var(--surface)",
          color: open ? "var(--accent)" : "var(--ink2)", fontWeight: 500,
        }}>
          <StopwatchIcon size={16} />
          <span className="logtime-label">Log time</span>
        </button>
      )}

      {open && (
        <div className="logtime-panel" role="dialog" aria-label="Log time" style={{
          // Its rows wrap, so it needn't be wide. Placed against the window,
          // not the button: the account menu sits right of the button, and a
          // panel hung off the button's edge ran off the left on a narrow window.
          position: "fixed", top: panelAt.top, right: panelAt.right, zIndex: 45,
          width: `min(${PANEL_W}px, calc(100vw - 24px))`, boxSizing: "border-box", padding: "14px 16px 16px",
          // Accent all the way round: open, it's a tool laid over the page, and
          // should never be mistaken for part of it.
          background: "var(--raised)", border: "2px solid var(--accent)", borderRadius: 14,
          boxShadow: "var(--shadowOverlay)",
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, flex: "none", whiteSpace: "nowrap" }}>{running ? "Timer running" : "Log time"}</span>
            <span style={{ fontSize: 11.5, color: "var(--muted)" }}>
              {running ? "Change anything while it runs — project, task, start time — then stop to log it." : "Say what you're doing, where it goes, then a length — or Start a timer."}{" "}
              Press T to open this from any page; Esc closes it.
            </span>
          </div>
          <QuickLogBar embedded onDone={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

/** Quick Add into Todoist — the header twin of pressing Q. */
function QuickAddButton() {
  const { quickAddHere } = useTaskEditor();
  return (
    <button onClick={quickAddHere} title="Quick add a task (Q)" aria-label="Quick add a task" style={{
      ...pillBtn, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontWeight: 500,
    }}>
      <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
      <span className="logtime-label">Add task</span>
    </button>
  );
}

const pillBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 12px", borderRadius: 999,
  border: "none", background: "transparent", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
};
